import { ElementType, memo, MouseEventHandler, ReactNode, useCallback, useContext, useState } from 'react';
import clsx from 'clsx';
import { Button, ButtonProps } from 'src/common/Button/Button';
import { useOutsideClick } from 'src/modules/common/hooks/useOutsideClick';
import { RuntimeContext } from 'src/runtimeContext';
import { createPortal } from 'react-dom';
import { autoUpdate, useFloating } from '@floating-ui/react';

type Props = {
    label?: ReactNode;
    icon: ReactNode;
    size?: ButtonProps<ElementType>['size'];
    kind?: ButtonProps<ElementType>['kind'];
    children: ReactNode;
}

export const OverflowMenu = memo(({ label, icon, kind = 'tertiary', size, children }: Props) => {
    const { modalRoot } = useContext(RuntimeContext);

    const [opened, setOpened] = useState(false);

    const { x, y, strategy, refs } = useFloating({
        open: opened,
        onOpenChange: setOpened,
        placement: 'bottom-end',
        whileElementsMounted: autoUpdate,
    });

    const handleButtonClose = useCallback(() => {
        setOpened(false);
    }, []);

    const ref = useOutsideClick<HTMLDivElement>(handleButtonClose);

    const handleButtonToggle: MouseEventHandler<HTMLButtonElement> = useCallback((e) => {
        e.stopPropagation();
        setOpened((prev) => !prev);
    }, []);

    return (
        <div ref={ref} className={clsx({ 'vss-overflow-menu': true, 'vss-overflow-menu--opened': opened })}>
            <Button
                buttonRef={refs.setReference}
                kind={kind}
                size={size}
                iconOnly={!label}
                icon={icon ? (
                    <div className="vss-overflow-menu__button-icon">
                        {icon}
                    </div>
                ) : null}
                className="vss-overflow-menu__button"
                onClick={handleButtonToggle}
            >
                {label}
            </Button>

            {createPortal(
                (
                    <div
                        ref={refs.setFloating}
                        className={clsx({
                            'vss-overflow-menu__list': true,
                            'vss-overflow-menu__list--opened': opened,
                        })}
                        style={{
                            position: strategy,
                            top: y ?? 0,
                            left: x ?? 0,
                        }}
                    >
                        {children}
                    </div>
                ),
                modalRoot,
            )}
        </div>
    );
});
